import * as Api from '@ViewModels';
import { toast } from 'sonner';
import { invalidateRegEdActionNeeded } from './useRegEdActionNeededQuery';
import { invalidateRegEdSubmissions } from './useRegEdSubmissionsQuery';
import { requestRegEdLeadTimeOverride } from './regEdLeadTimeOverridePrompt';
import {
	getRegEdComplianceErrorCode,
	IRegEdSubmissionCreateRequest,
	IRegEdSubmissionDeferRequest,
	IRegEdSubmissionInput,
	IRegEdSubmissionView,
	isRegEdLeadTimeReasonFixable,
	RegEdComplianceContentType,
	RegEdComplianceErrorCode,
} from './types';

export interface ISubmitRegEdComplianceArgs {
	userSession: Api.UserSessionContext;
	parentType: RegEdComplianceContentType;
	parentId: string | undefined;
	/** From the created parent's `approvalRequests[0].id`; falsy means the server built none to file against. */
	approvalRequestId: string | null | undefined;
	metadata: IRegEdSubmissionInput | null | undefined;
	revisesApprovalRequestId?: string | null;
	impersonationContext?: Api.IImpersonationContext;
	logApiError?: (action: string, error?: Api.IOperationResultNoValue) => void;
	/** Failure-toast verb — the parent was already 'scheduled' (default) or 'sent'. */
	action?: 'scheduled' | 'sent';
	/** How to surface the resubmit message; defaults to a sonner toast (admin flows pass errorMessages.push). */
	onError?: (message: string) => void;
	/** Only when the requested review date is inside the firm's notice window; the reviewer reads it. */
	reviewLeadTimeOverrideReason?: string;
	/**
	 * Called instead of the generic resubmit message when a reason would resolve the rejection, so the
	 * caller can collect one and retry. Without it the server's own message is surfaced.
	 */
	onReviewLeadTimeRejected?: (rejection: { errorCode: RegEdComplianceErrorCode; message: string }) => void;
	/**
	 * Runs once the submission has actually landed — including when it lands from the lead-time override
	 * prompt's own retry, long after this call returned. Work that must follow a successful filing belongs
	 * here rather than after the returned boolean, which reports only the first attempt.
	 */
	onFiled?: () => boolean | void | Promise<boolean | void>;
	/**
	 * The lead-time override prompt now owns the outcome: this call returns false, but the submission has
	 * not failed and must not be replayed. Callers that park on a retry affordance should skip it.
	 */
	onDeferredToLeadTimeOverride?: () => void;
	/**
	 * The override prompt was dismissed without a reason, so the submission handed to it will never be
	 * made. Callers that stood down a retry affordance for the handover restore it here.
	 */
	onLeadTimeOverrideDismissed?: () => void;
	/**
	 * The filing was left to the account's user — either asked for, or because the API refused an admin's
	 * filing and recorded the handover itself. Nothing was filed, so `onFiled` does not run. Without it, a
	 * default toast says who still owes the questions.
	 */
	onDeferredToAccountUser?: () => void | Promise<void>;
}

/**
 * The 2-step RegEd submission POST (`compliance/regEd/submissions`), isolated so a failure never fails
 * the already-created parent. Returns true when the filing succeeded or there was nothing to file;
 * false when it was dropped (no approval request) or errored — both emit `RegEdSubmission-*` telemetry
 * and a resubmit message so ops keep signal and the advisor is told to reopen and resubmit.
 */
export const submitRegEdCompliance = async ({
	userSession,
	parentType,
	parentId,
	approvalRequestId,
	metadata,
	revisesApprovalRequestId,
	impersonationContext,
	logApiError,
	action = 'scheduled',
	onError,
	reviewLeadTimeOverrideReason,
	onReviewLeadTimeRejected,
	onFiled,
	onDeferredToLeadTimeOverride,
	onLeadTimeOverrideDismissed,
	onDeferredToAccountUser,
}: ISubmitRegEdComplianceArgs): Promise<boolean> => {
	const noun = parentType === RegEdComplianceContentType.Email ? 'campaign' : 'post';
	const surface = onError ?? ((message: string) => toast.error(message));
	const resubmitMessage = `${action === 'sent' ? 'Sent' : 'Scheduled'}, but the compliance submission didn’t go through — reopen the ${noun} to resubmit.`;
	const completeDeferral = async () => {
		invalidateRegEdActionNeeded();
		invalidateRegEdSubmissions(parentId);
		if (onDeferredToAccountUser) {
			await onDeferredToAccountUser();
		} else {
			// Hosts only report "scheduled", which says nothing about who still owes the questions.
			toast.info("It's waiting on the account's user to answer the compliance questions.");
		}
	};

	// Ahead of the metadata guard below, which would otherwise treat a deferral's empty answers as
	// nothing to file — or, on a revision, as a failure.
	if (metadata?.deferToAccountUser) {
		if (!parentId || !approvalRequestId) {
			logApiError?.('RegEdDeferral-MissingApprovalRequest');
			surface(resubmitMessage);
			return false;
		}
		const deferralRequest: IRegEdSubmissionDeferRequest = {
			approvalRequestId,
			parentId,
			parentType,
			revisesApprovalRequestId: revisesApprovalRequestId ?? null,
		};
		try {
			await userSession.webServiceHelper.callAsync<IRegEdSubmissionView>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'compliance/regEd/submissions/defer', impersonationContext }),
				'POST',
				deferralRequest
			);
		} catch (deferralError) {
			const apiError = Api.asApiError(deferralError);
			logApiError?.('RegEdDeferral-Error', apiError);
			surface(apiError?.systemMessage || resubmitMessage);
			return false;
		}
		await completeDeferral();
		return true;
	}

	if (!metadata) {
		// Nothing to file is an ordinary outcome for a first submission, but a revision has already
		// reopened the round by this point — reporting success would leave it open with the submission
		// never repointed, and no caller state would say otherwise.
		if (revisesApprovalRequestId) {
			logApiError?.('RegEdSubmission-MissingMetadataOnRevision');
			surface(resubmitMessage);
			return false;
		}
		return true;
	}

	if (!parentId || !approvalRequestId) {
		logApiError?.('RegEdSubmission-MissingApprovalRequest');
		surface(resubmitMessage);
		return false;
	}

	// The prompt bundles the reason with the answers so the flows in between carry one object; it is a
	// sibling of `metadata` on the wire, never part of the AdTrax answers.
	const { reviewLeadTimeOverrideReason: bundledReason, earliestSendDate: _sendFloor, ...answersOnly } = metadata;
	const overrideReason = reviewLeadTimeOverrideReason ?? bundledReason;
	const submissionRequest: IRegEdSubmissionCreateRequest = {
		parentType,
		parentId,
		approvalRequestId,
		metadata: answersOnly,
		revisesApprovalRequestId: revisesApprovalRequestId ?? null,
		...(overrideReason ? { reviewLeadTimeOverrideReason: overrideReason } : {}),
	};
	try {
		await userSession.webServiceHelper.callAsync<IRegEdSubmissionView>(
			Api.ImpersonationBroker.composeApiUrl({ urlPath: 'compliance/regEd/submissions', impersonationContext }),
			'POST',
			submissionRequest
		);
		// The filing answers whatever the to-do surfaces were showing for this piece; without this the
		// resolved item lingers in the dashboard card and the campaigns-list join until a refetch.
		invalidateRegEdActionNeeded();
	} catch (submissionError) {
		const apiError = Api.asApiError(submissionError);
		const errorCode = getRegEdComplianceErrorCode(apiError);
		// The API recorded the handover before refusing, so the piece is already waiting on the account's user.
		if (errorCode === RegEdComplianceErrorCode.FilingRequiresAccountUser) {
			await completeDeferral();
			return true;
		}
		logApiError?.('RegEdSubmission-Error', apiError);
		// A compliance rejection is the advisor's to fix, so its own message beats the generic
		// "reopen and resubmit" — that would send them back to a form that would fail the same way.
		if (errorCode === undefined) {
			surface(resubmitMessage);
			return false;
		}
		const message = apiError?.systemMessage || resubmitMessage;
		if (!isRegEdLeadTimeReasonFixable(errorCode)) {
			surface(message);
			return false;
		}
		if (onReviewLeadTimeRejected) {
			onReviewLeadTimeRejected({ errorCode, message });
			return false;
		}
		// Retries itself with the reason, so every caller gets the override without wiring a dialog.
		const prompted = requestRegEdLeadTimeOverride({
			errorCode,
			message,
			retry: (reason: string) =>
				submitRegEdCompliance({
					userSession,
					parentType,
					parentId,
					approvalRequestId,
					metadata,
					revisesApprovalRequestId,
					impersonationContext,
					logApiError,
					action,
					onError,
					onFiled,
					onDeferredToAccountUser,
					onDeferredToLeadTimeOverride,
					onLeadTimeOverrideDismissed,
					onReviewLeadTimeRejected,
					reviewLeadTimeOverrideReason: reason,
				}),
			onDismissed: onLeadTimeOverrideDismissed,
		});
		if (prompted) {
			// The prompt's retry carries onFiled, so a reason supplied there still completes the filing.
			onDeferredToLeadTimeOverride?.();
		} else {
			surface(message);
		}
		return false;
	}
	// Outside the try: a throw from the caller's post-filing work is not a submission failure, and
	// reporting it as one would send the retry back through the repoint, which cannot be replayed.
	// Its verdict still counts though — the submission is only as good as the work that completes it,
	// and callers that report success on their own behalf would otherwise contradict the caller's UI.
	const completed = await onFiled?.();
	return completed !== false;
};
